import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { CampaignManagementService } from '../../../services/campaignManagementService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CreateCampaignModal = ({ isOpen, onClose, onCampaignCreated }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'draft',
    startDate: '',
    endDate: '',
    targetScans: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!user?.id) {
      setError('You must be logged in to create campaigns');
      return;
    }

    if (!formData?.name?.trim()) {
      setError('Campaign name is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const campaignData = {
        name: formData?.name?.trim(),
        description: formData?.description?.trim() || null,
        status: formData?.status || 'draft',
        start_date: formData?.startDate || null,
        end_date: formData?.endDate || null,
        target_scans: formData?.targetScans ? parseInt(formData?.targetScans, 10) : 0,
        owner_id: user?.id
      };

      const { data, error: createError } = await CampaignManagementService?.createCampaign(campaignData);

      if (createError) {
        setError(createError);
        return;
      }

      // Reset form
      setFormData({
        name: '',
        description: '',
        status: 'draft',
        startDate: '',
        endDate: '',
        targetScans: ''
      });

      // Notify parent component first (triggers refresh)
      onCampaignCreated?.(data);
      
    } catch (error) {
      setError(`Failed to create campaign: ${error?.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Icon name="Target" size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create New Campaign</h2>
              <p className="text-sm text-gray-600">Set up a new QR code marketing campaign</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <Icon name="AlertTriangle" size={20} className="text-red-600 mt-0.5 mr-3" />
                <div>
                  <p className="font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Campaign Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter campaign name"
              value={formData?.name || ''}
              onChange={handleInputChange}
              className="w-full"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              placeholder="Describe your campaign goals and strategy"
              value={formData?.description || ''}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Initial Status
            </label>
            <Select
              id="status"
              name="status"
              value={formData?.status || 'draft'}
              onChange={handleInputChange}
              options={statusOptions}
              className="w-full"
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <Input
                id="startDate"
                name="startDate"
                type="date"
                value={formData?.startDate || ''}
                onChange={handleInputChange}
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <Input
                id="endDate"
                name="endDate"
                type="date"
                value={formData?.endDate || ''}
                onChange={handleInputChange}
                className="w-full"
              />
            </div>
          </div>

          {/* Target Scans */}
          <div>
            <label htmlFor="targetScans" className="block text-sm font-medium text-gray-700 mb-1">
              Target Scans
            </label>
            <Input
              id="targetScans"
              name="targetScans"
              type="number"
              placeholder="1000"
              value={formData?.targetScans || ''}
              onChange={handleInputChange}
              className="w-full"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">Set a goal for total scans (optional)</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isLoading || !formData?.name?.trim()}
              loading={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaignModal;